// Shared by both forks.
import React from 'react';
import { EMPTY_RESULTS } from '../constants';
import type { SearchOptionType, SelectedSearchOption } from '../helpers/types';
import { ResultsCategory, type SelectedResultsCategory } from '../redesign/constants';
import { getCategoryDisabledReason } from '../redesign/Results/utils';
import type { AssetsSearchResults, SearchResults, TagsSearchResults } from '../types';

// The old fork has no result categories; it keeps one here that it never reads.
export type SearchState = {
	searchTerm: string;
	isLoadingResults: boolean;
	searchResults: SearchResults;
	selectedSearchOptions: SelectedSearchOption[];
	selectedResultsCategory: SelectedResultsCategory;
};

export type SearchAction =
	| { type: 'termChanged'; term: React.SetStateAction<string> }
	| { type: 'searchStarted' }
	| { type: 'assetResultsReceived'; assets: AssetsSearchResults; tags: TagsSearchResults }
	| { type: 'groupResultsReceived'; groups: Partial<SearchResults> }
	| { type: 'searchSucceeded'; canShowBrandguidelines: boolean }
	| { type: 'searchFailed' }
	| { type: 'searchSettled' }
	| { type: 'resultsDiscarded' }
	| { type: 'resultsCleared' }
	| { type: 'modeReset' }
	| { type: 'scopeReset' }
	| { type: 'stickySearchRestored'; term: string; options: SelectedSearchOption[] }
	| { type: 'searchOptionsChanged'; options: React.SetStateAction<SelectedSearchOption[]> }
	| { type: 'searchOptionRemoved'; key: SearchOptionType }
	| { type: 'resultsCategoryChanged'; category: React.SetStateAction<SelectedResultsCategory> };

export const initialSearchState: SearchState = {
	searchTerm: '',
	isLoadingResults: false,
	searchResults: EMPTY_RESULTS,
	selectedSearchOptions: [],
	selectedResultsCategory: null,
};

const resolve = <T>(next: React.SetStateAction<T>, current: T): T =>
	typeof next === 'function' ? (next as (value: T) => T)(current) : next;

// Returns the same state when nothing changes, so React skips the render.
const update = (state: SearchState, changes: Partial<SearchState>): SearchState =>
	(Object.keys(changes) as (keyof SearchState)[]).every((key) =>
		Object.is(state[key], changes[key]),
	)
		? state
		: { ...state, ...changes };

const getCategoryAfterSearch = (state: SearchState, canShowBrandguidelines: boolean) => {
	const isEnabled = (category: ResultsCategory) =>
		getCategoryDisabledReason(category, state) === null;
	const kept = state.selectedResultsCategory ?? ResultsCategory.Assets;

	if (isEnabled(kept)) {
		return kept;
	}

	const categories = [
		ResultsCategory.Assets,
		ResultsCategory.Collections,
		...(canShowBrandguidelines ? [ResultsCategory.Guidelines] : []),
	];

	return categories.find(isEnabled) ?? ResultsCategory.Assets;
};

export function searchReducer(state: SearchState, action: SearchAction): SearchState {
	switch (action.type) {
		case 'termChanged':
			return update(state, { searchTerm: resolve(action.term, state.searchTerm) });
		case 'searchStarted':
			return update(state, { isLoadingResults: true });
		case 'assetResultsReceived':
			return update(state, {
				searchResults: { ...state.searchResults, assets: action.assets, tags: action.tags },
			});
		case 'groupResultsReceived':
			return update(state, { searchResults: { ...state.searchResults, ...action.groups } });
		case 'searchSucceeded':
			return update(state, {
				selectedResultsCategory: getCategoryAfterSearch(
					state,
					action.canShowBrandguidelines,
				),
			});
		case 'searchFailed':
			return update(state, { searchResults: EMPTY_RESULTS });
		case 'searchSettled':
			return update(state, { isLoadingResults: false });
		case 'resultsDiscarded':
			return update(state, {
				isLoadingResults: false,
				searchResults: EMPTY_RESULTS,
				selectedResultsCategory: null,
			});
		case 'resultsCleared':
			return update(state, {
				searchTerm: '',
				isLoadingResults: false,
				searchResults: EMPTY_RESULTS,
				selectedResultsCategory: null,
			});
		case 'modeReset':
			return update(state, {
				searchResults: EMPTY_RESULTS,
				selectedResultsCategory: null,
				selectedSearchOptions: [],
			});
		case 'scopeReset':
			return update(state, { selectedResultsCategory: null, selectedSearchOptions: [] });
		case 'stickySearchRestored':
			return update(state, {
				searchTerm: action.term,
				...(action.options.length > 0 && { selectedSearchOptions: action.options }),
			});
		case 'searchOptionsChanged':
			return update(state, {
				selectedSearchOptions: resolve(action.options, state.selectedSearchOptions),
			});
		case 'searchOptionRemoved':
			return update(state, {
				selectedSearchOptions: state.selectedSearchOptions.filter(
					(option) => option.key !== action.key,
				),
			});
		case 'resultsCategoryChanged':
			return update(state, {
				selectedResultsCategory: resolve(action.category, state.selectedResultsCategory),
			});
		default:
			return state;
	}
}
